import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useVehicle } from '../../contexts/VehicleContext';
import VehicleLookup from './VehicleLookup';
import { FaMapMarkerAlt, FaClock, FaStar, FaCar, FaPlus } from 'react-icons/fa';
import { authService, jobCardService, publicGarageService, getDocumentUrl } from '../../services/apiService';

const ServiceBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vehicles, addVehicle } = useVehicle();

  const [step, setStep] = useState(1);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [symptoms, setSymptoms] = useState('');
  const [preferredServiceDate, setPreferredServiceDate] = useState(() =>
    new Date(Date.now() + 86400000).toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState('');
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [showVehicleLookup, setShowVehicleLookup] = useState(false);

  useEffect(() => {
    if (vehicles.length === 0) {
      setShowVehicleLookup(true);
    }
  }, [vehicles.length]);

  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [garages, setGarages] = useState([]);
  const [loadingGarages, setLoadingGarages] = useState(false);
  const [booking, setBooking] = useState(false);

  const preselectedGarageId = location.state?.preselectedGarageId;
  const hasPreselectedGarage = !!preselectedGarageId;

  // Total steps: 2 if garage preselected (Vehicle → Details), else 3 (Vehicle → Garage → Details)
  const totalSteps = hasPreselectedGarage ? 2 : 3;
  const stepLabels = hasPreselectedGarage
    ? ['Vehicle', 'Service Details']
    : ['Vehicle', 'Garage', 'Service Details'];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingGarages(true);
        const list = await publicGarageService.list();
        if (mounted) setGarages(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setGarages([]);
      } finally {
        if (mounted) setLoadingGarages(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const garagesForUI = useMemo(() => {
    return (garages || []).map((g, idx) => ({
      id: g._id || g.id,
      name: g.name || 'Garage',
      address: g.location || '',
      rating: g.rating ?? (4.4 + ((idx % 5) * 0.1)),
      reviews: g.reviews ?? (120 + (idx * 7)),
      distance: g.distance ?? '—',
    }));
  }, [garages]);

  // When coming from GarageSelect, pre-select garage and go to step 2 (details) after vehicle
  useEffect(() => {
    if (!preselectedGarageId || loadingGarages || garagesForUI.length === 0) return;
    const match = garagesForUI.find((g) => String(g.id) === String(preselectedGarageId));
    if (match) setSelectedGarage(match);
  }, [preselectedGarageId, loadingGarages, garagesForUI]);

  const handleVehicleSelected = (vehicle) => {
    // Simply select the vehicle for this booking.
    // Creation of a new vehicle (persisted) is handled explicitly inside VehicleLookup
    // when the user uses "Add New Vehicle".
    setSelectedVehicle(vehicle);
    setShowVehicleLookup(false);
    // Next: Details if garage preselected, else Garage selection
    setStep(hasPreselectedGarage ? 2 : 2);
  };

  const handleGarageSelect = (garage) => {
    setSelectedGarage(garage);
    setStep(3);
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setUploadError('');
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        // Read file as base64
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result;
            const commaIndex = typeof result === 'string' ? result.indexOf(',') : -1;
            resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
          };
          reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });

        const stored = await authService.uploadFile(base64, file.name, file.type || 'image/jpeg');
        const url = getDocumentUrl(stored);
        uploaded.push(url);
      }
      setPhotos((prev) => [...prev, ...uploaded]);
    } catch (err) {
      setUploadError(err.message || 'Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
      // Reset input so same file can be chosen again if needed
      event.target.value = '';
    }
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBookService = async () => {
    if (!selectedVehicle || !selectedGarage?.id || !symptoms?.trim() || !preferredServiceDate) {
      alert('Please fill all required fields (vehicle, garage, complaint description, preferred date)');
      return;
    }
    try {
      setBooking(true);
      const payload = {
        garageId: selectedGarage.id,
        vehicle: {
          registrationNumber: selectedVehicle.registrationNumber || selectedVehicle.regNo || '',
          make: selectedVehicle.make || '',
          model: selectedVehicle.model || '',
          variant: selectedVehicle.variant || '',
          year: selectedVehicle.year || '',
          vin: selectedVehicle.vin || '',
        },
        complaintDescription: symptoms.trim(),
        preferredServiceDate,
        notes: notes.trim() || undefined,
        customerPhotos: photos,
      };
      const jobCard = await jobCardService.create(payload);
      const id = jobCard?._id || jobCard?.id;
      navigate(`/service/job/${id}`);
    } catch (e) {
      alert(e.message || 'Failed to create service request');
    } finally {
      setBooking(false);
    }
  };

  const goBack = () => {
    if (step === 1) navigate(-1);
    else setStep(step - 1);
  };

  const detailsStepNumber = hasPreselectedGarage ? 2 : 3;

  return (
    <div className="checkout-light-page w-full bg-slate-50 py-4 sm:py-8">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Create Service Request</h1>
          <p className="text-gray-600">Select vehicle and provide service details. Job will be assigned to the garage.</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            {stepLabels.map((label, idx) => {
              const s = idx + 1;
              const isActive = step >= s;
              return (
                <React.Fragment key={s}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                        isActive ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {step > s ? '✓' : s}
                    </div>
                    <span className="text-xs mt-2 text-gray-600 font-medium">{label}</span>
                  </div>
                  {s < totalSteps && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded ${
                        step > s ? 'bg-red-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step 1: Vehicle — saved list or Find Your Vehicle */}
        {step === 1 && (
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-4 sm:px-6 sm:py-5">
              <h2 className="text-lg font-bold text-gray-900 sm:text-xl">
                {showVehicleLookup ? 'Find Your Vehicle' : 'Select Vehicle'}
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                {showVehicleLookup
                  ? 'Search by registration, VIN, or add a new vehicle to continue.'
                  : 'Choose a saved vehicle or find another one.'}
              </p>
            </div>
            <div className="space-y-4 p-4 sm:p-6">
              {showVehicleLookup ? (
                <VehicleLookup
                  embedded
                  showTitle={false}
                  onVehicleSelected={handleVehicleSelected}
                  onCancel={vehicles.length > 0 ? () => setShowVehicleLookup(false) : undefined}
                />
              ) : (
                <>
                  {vehicles.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700">Your vehicles</p>
                      <div className="space-y-2">
                        {vehicles.map((v) => {
                          const vid = v._id || v.id;
                          const selId = selectedVehicle?._id || selectedVehicle?.id;
                          return (
                            <button
                              key={vid}
                              type="button"
                              onClick={() => handleVehicleSelected(v)}
                              className={`flex w-full items-center gap-3 rounded-lg border-2 p-3 text-left transition-all sm:gap-4 sm:p-4 ${
                                selId != null && selId === vid
                                  ? 'border-red-600 bg-red-50'
                                  : 'border-gray-200 bg-white hover:border-red-300 hover:bg-red-50'
                              }`}
                            >
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 sm:h-12 sm:w-12">
                                <FaCar className="text-lg sm:text-xl" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-semibold text-gray-900 break-words">
                                  {v.make} {v.model} {v.variant}
                                </p>
                                <p className="text-xs text-gray-500 sm:text-sm">
                                  Reg: {v.registrationNumber || v.regNo || '—'}
                                  {v.year ? ` · ${v.year}` : ''}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setShowVehicleLookup(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-3 text-sm font-medium text-gray-700 transition-colors hover:border-red-400 hover:bg-red-50 hover:text-red-600 sm:text-base"
                  >
                    <FaPlus />
                    Find another vehicle
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Garage Selection (only when no preselected garage) */}
        {step === 2 && !hasPreselectedGarage && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Select Garage</h2>
              <p className="text-sm text-gray-500 mt-1">Choose a workshop for your service</p>
            </div>
            <div className="p-6">
              {loadingGarages ? (
                <div className="py-10 text-center text-gray-500">Loading garages…</div>
              ) : garagesForUI.length === 0 ? (
                <div className="py-10 text-center text-gray-500">No garages available right now.</div>
              ) : (
                <div className="space-y-4">
                  {garagesForUI.map((garage) => (
                    <div
                      key={garage.id}
                      onClick={() => handleGarageSelect(garage)}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        selectedGarage?.id === garage.id
                          ? 'border-red-600 bg-red-50'
                          : 'border-gray-200 hover:border-red-300'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{garage.name}</h3>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-2">
                            <span className="flex items-center gap-1">
                              <FaMapMarkerAlt /> {garage.address || '—'}
                            </span>
                            <span className="flex items-center gap-1">
                              <FaClock /> {garage.distance}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FaStar className="text-amber-500" />
                            <span className="font-semibold">{Number(garage.rating).toFixed(1)}</span>
                            <span className="text-gray-500">({garage.reviews} reviews)</span>
                          </div>
                        </div>
                        <span className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg">
                          Select
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3 (or 2 when preselected): Service Details */}
        {step === detailsStepNumber && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Service Request Details</h2>
              <p className="text-sm text-gray-500 mt-1">Describe the issue and preferred slot</p>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Vehicle</p>
                  <p className="font-semibold text-gray-900 mt-1">
                    {selectedVehicle?.make} {selectedVehicle?.model} {selectedVehicle?.variant}
                  </p>
                  <p className="text-sm text-gray-600">
                    Reg: {selectedVehicle?.registrationNumber || selectedVehicle?.regNo || '—'}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Garage</p>
                  <p className="font-semibold text-gray-900 mt-1">{selectedGarage?.name || '—'}</p>
                  <p className="text-sm text-gray-600">{selectedGarage?.address || '—'}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Complaint description *
                </label>
                <textarea
                  value={symptoms}
                  onChange={(e) => setSymptoms(e.target.value)}
                  placeholder="e.g., Brake noise, AC not cooling, Engine overheating..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred service date *
                  </label>
                  <input
                    type="datetime-local"
                    value={preferredServiceDate}
                    onChange={(e) => setPreferredServiceDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Optional notes</label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Anything the garage should know…"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload issue photos (optional)
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Add photos of the problem areas (max a few images). This helps the garage diagnose faster.
                </p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                  disabled={uploading}
                />
                {uploadError && (
                  <p className="mt-2 text-xs text-red-600 font-medium">
                    {uploadError}
                  </p>
                )}
                {photos.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {photos.map((url, idx) => (
                      <div
                        key={url + idx}
                        className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
                      >
                        <img
                          src={url}
                          alt=""
                          className="w-full h-24 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {uploading && (
                  <p className="mt-2 text-xs text-gray-500">
                    Uploading images…
                  </p>
                )}
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="button"
                  onClick={goBack}
                  className="flex-1 py-3 px-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleBookService}
                  disabled={booking}
                  className="flex-1 py-3 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {booking ? 'Creating…' : 'Create Service Request'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceBooking;
